import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Layout } from '@organisms/Layout';
import { AdminShell } from '@organisms/AdminShell';
import { RoleProtectedRoute } from '@/components/hoc/RoleProtectedRoute';
import { LandingPage } from '@pages/public/LandingPage';
import { SearchPage } from '@pages/public/SearchPage';
import { LoginPage } from '@pages/auth/LoginPage';
import { SsoCallbackPage } from '@pages/auth/SsoCallbackPage';
import { OverviewSection } from '@pages/admin/sections/OverviewSection';
import { UsersSection } from '@pages/admin/sections/UsersSection';
import { CategoriesSection } from '@pages/admin/sections/CategoriesSection';
import { SettingsSection } from '@pages/admin/sections/SettingsSection';
import { WhatsAppSection } from '@pages/admin/sections/WhatsAppSection';
import { SystemStatusSection } from '@pages/admin/sections/SystemStatusSection';
import { AdminAdsPage } from '@pages/admin/AdminAdsPage';
import { AdminRemindersSection } from '@pages/admin/sections/AdminRemindersSection';
import { BillingSection } from '@pages/admin/sections/BillingSection';
import { WholesalerShell } from '@organisms/WholesalerShell';
import { OverviewSection as WholesalerOverviewSection } from '@pages/wholesaler/sections/OverviewSection';
import { ProductsSection as WholesalerProductsSection } from '@pages/wholesaler/sections/ProductsSection';
import { OrdersSection as WholesalerOrdersSection } from '@pages/wholesaler/sections/OrdersSection';
import { MessagesSection as WholesalerMessagesSection } from '@pages/wholesaler/sections/MessagesSection';
import { ServiceAreasSection } from '@pages/wholesaler/sections/ServiceAreasSection';
import { CampaignsSection } from '@pages/wholesaler/sections/CampaignsSection';
import { AccountingSection } from '@pages/wholesaler/sections/AccountingSection';
import { AdsSection as WholesalerAdsSection } from '@pages/wholesaler/sections/AdsSection';
import { ReviewsSection as WholesalerReviewsSection } from '@pages/wholesaler/sections/ReviewsSection';
import { SettingsSection as WholesalerSettingsSection } from '@pages/wholesaler/sections/SettingsSection';
import { RemindersSection as WholesalerRemindersSection } from '@pages/wholesaler/sections/RemindersSection';
import { RestaurantDashboard } from '@pages/restaurant/RestaurantDashboard';
import { RestaurantMessagesPage } from '@pages/restaurant/RestaurantMessagesPage';
import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useCartStore } from '@store/cartStore';
import { useAuthStore } from '@store/authStore';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      retry: 1,
    },
  },
});

function CartSync() {
  const { pathname } = useLocation();
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const syncFromBackend = useCartStore((s) => s.syncFromBackend);

  useEffect(() => {
    if (pathname === '/auth/sso/callback' || !isAuthenticated) {
      return;
    }
    void syncFromBackend();
  }, [syncFromBackend, pathname, isAuthenticated]);

  return null;
}

function LegacyDashboardRedirect({ to }: { to: string }) {
  return <Navigate to={to} replace />;
}

export function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <CartSync />
        <Routes>
          <Route element={<Layout />}>
            <Route index element={<LandingPage />} />
            <Route path="search" element={<SearchPage />} />
            <Route path="login" element={<LoginPage />} />
            <Route path="auth/sso/callback" element={<SsoCallbackPage />} />

            <Route element={<RoleProtectedRoute allowedRoles={['admin']} />}>
              <Route element={<AdminShell />}>
                <Route path="admin" element={<OverviewSection />} />
                <Route path="admin/users" element={<UsersSection />} />
                <Route path="admin/categories" element={<CategoriesSection />} />
                <Route path="admin/orders" element={<Navigate to="/admin" replace />} />
                <Route path="admin/ads" element={<AdminAdsPage />} />
                <Route path="admin/whatsapp" element={<WhatsAppSection />} />
                <Route path="admin/reminders" element={<AdminRemindersSection />} />
                <Route path="admin/billing" element={<BillingSection />} />
                <Route path="admin/settings" element={<SettingsSection />} />
                <Route path="admin/system" element={<SystemStatusSection />} />
              </Route>
              <Route path="dashboard/admin" element={<LegacyDashboardRedirect to="/admin" />} />
            </Route>

            <Route element={<RoleProtectedRoute allowedRoles={['wholesaler']} />}>
              <Route element={<WholesalerShell />}>
                <Route path="wholesaler" element={<WholesalerOverviewSection />} />
                <Route path="wholesaler/products" element={<WholesalerProductsSection />} />
                <Route path="wholesaler/orders" element={<WholesalerOrdersSection />} />
                <Route path="wholesaler/messages" element={<WholesalerMessagesSection />} />
                <Route path="wholesaler/messages/:partnerId" element={<WholesalerMessagesSection />} />
                <Route path="wholesaler/service-areas" element={<ServiceAreasSection />} />
                <Route path="wholesaler/campaigns" element={<CampaignsSection />} />
                <Route path="wholesaler/accounting" element={<AccountingSection />} />
                <Route path="wholesaler/ads" element={<WholesalerAdsSection />} />
                <Route path="wholesaler/reviews" element={<WholesalerReviewsSection />} />
                <Route path="wholesaler/reminders" element={<WholesalerRemindersSection />} />
                <Route path="wholesaler/settings" element={<WholesalerSettingsSection />} />
              </Route>
              <Route path="dashboard/wholesaler" element={<LegacyDashboardRedirect to="/wholesaler" />} />
            </Route>

            <Route element={<RoleProtectedRoute allowedRoles={['restaurant']} />}>
              <Route path="restaurant" element={<RestaurantDashboard />} />
              <Route path="restaurant/messages/:partnerId?" element={<RestaurantMessagesPage />} />
              <Route path="dashboard/restaurant" element={<LegacyDashboardRedirect to="/restaurant" />} />
            </Route>

            <Route path="*" element={<Navigate to="/" replace />} />
          </Route>
        </Routes>
      </BrowserRouter>
    </QueryClientProvider>
  );
}
